/**
 * Per-pod detail drawer - Phase 7 batch B (2026-05-16).
 *
 * Modal overlay that surfaces full pod telemetry + four actions:
 *   • Scale knobs - pushes a config-override patch (allowlist of 6 keys)
 *   • Diagnose    - synthesises a structured report from telemetry
 *   • Update      - pushes a config-override with targetVersion +
 *                   rolloutStrategy
 *   • Close
 *
 * Receives pod id via prop; lazy-fetches the full detail on open. Each
 * action emits an idempotent POST so accidental double-clicks don't
 * push duplicate overrides.
 */
import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  X,
  RefreshCw,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Clock,
  HelpCircle,
  PauseCircle,
  PlayCircle,
  Sliders,
  Stethoscope,
  Upload,
  Activity as ActivityIcon,
  Server,
  ShieldAlert,
  Send,
  Terminal,
} from 'lucide-react';
import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/primitives';
import {
  getEdgePod,
  pushEdgeConfigOverride,
  diagnoseEdgePod,
  triggerEdgeUpdate,
  decommissionEdgePod,
  type EdgePodDetail,
  type DiagnoseReport,
  type DiagnoseCheckStatus,
  listEdgeCommands,
  enqueueEdgeCommand,
  type EdgeRolloutStrategy,
  type EdgeCommand,
  type EdgeCommandName,
  type EdgeCommandState,
} from '@/lib/api';
import { Trash2 } from 'lucide-react';

interface Props {
  podId: string | null;
  onClose: () => void;
  onMutated?: () => void; // Parent refreshes fleet list after mutations.
}

const COMPONENT_ICON: Record<string, typeof Server> = {
  discovery: Server,
  activity: ActivityIcon,
  detection: ShieldAlert,
};

const CHECK_VARIANT: Record<DiagnoseCheckStatus, 'success' | 'warning' | 'critical' | 'glass'> = {
  ok: 'success',
  warn: 'warning',
  fail: 'critical',
  unknown: 'glass',
};

const CHECK_ICON: Record<DiagnoseCheckStatus, typeof CheckCircle2> = {
  ok: CheckCircle2,
  warn: AlertTriangle,
  fail: XCircle,
  unknown: HelpCircle,
};

/**
 * `sent` is amber, not green. It means the command reached the device and we
 * have heard nothing back - which is the state an operator must chase, not the
 * one they can stop thinking about.
 */
const COMMAND_STATE_VARIANT: Record<EdgeCommandState, 'success' | 'warning' | 'critical' | 'glass'> = {
  queued: 'glass',
  sent: 'warning',
  acked: 'success',
  expired: 'critical',
};

interface ScaleForm {
  maxMemoryMb?: number;
  batchSize?: number;
  queueDepth?: number;
  classifierTierCap?: number;
  clickhouseHotStoreGb?: number;
  eventbusDrainIntervalMs?: number;
}

/**
 * Derive the kill-switch state shown in the drawer from the pod's active
 * config override.
 *
 * Exported so it can be tested without standing up the whole drawer - the
 * markup is not the risky part, this is. Two things it must get right:
 *
 * 1. `Object.hasOwn`, never a bare `patch['paused']`. The patch is
 *    operator-authored JSON off the wire, so a bare lookup walks the
 *    prototype chain; this repo already shipped that bug once, which is why
 *    shared/safe-lookup.ts exists.
 * 2. `=== true`, not truthiness. The value arrives as unknown from JSON, and
 *    a non-boolean must not read as paused.
 *
 * `pausePending` is only meaningful when the active override actually carries
 * a `paused` key - an unacknowledged SCALE override says nothing about the
 * kill switch and must not render "awaiting pod ack" next to it.
 */
export function deriveKillSwitchState(
  activeOverride: { patch: Record<string, unknown>; appliedAt: string | null } | null | undefined,
): { isPaused: boolean; pausePending: boolean } {
  const patch = activeOverride?.patch;
  const hasPaused = !!patch && Object.hasOwn(patch, 'paused');
  return {
    isPaused: hasPaused && patch!['paused'] === true,
    pausePending: hasPaused && !activeOverride!.appliedAt,
  };
}

export function EdgePodDetailDrawer({ podId, onClose, onMutated }: Props): JSX.Element {
  const [detail, setDetail] = useState<EdgePodDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<'overview' | 'commands' | 'scale' | 'diagnose' | 'update'>('overview');

  // Command queue (Command Plane Phase 5). `null` is "not loaded yet" and is
  // rendered differently from an empty list - "no commands ever issued" and
  // "we have not looked" are different answers.
  const [commands, setCommands] = useState<EdgeCommand[] | null>(null);
  const [cmdName, setCmdName] = useState<EdgeCommandName>('pause');
  const [cmdReason, setCmdReason] = useState('');
  const [cmdBusy, setCmdBusy] = useState(false);

  // Scale-knobs form state
  const [scaleForm, setScaleForm] = useState<ScaleForm>({});
  const [pauseBusy, setPauseBusy] = useState(false);
  const [scaleBusy, setScaleBusy] = useState(false);

  // Diagnose state
  const [diagnose, setDiagnose] = useState<DiagnoseReport | null>(null);
  const [diagnoseBusy, setDiagnoseBusy] = useState(false);

  // Decommission state (Phase 0 Day 4 - 2026-05-22)
  const [decommissionBusy, setDecommissionBusy] = useState(false);

  // Update form state
  const [targetVersion, setTargetVersion] = useState('');
  const [rolloutStrategy, setRolloutStrategy] = useState<EdgeRolloutStrategy>('canary');
  const [updateBusy, setUpdateBusy] = useState(false);
  const [updateOk, setUpdateOk] = useState<string | null>(null);

  useEffect(() => {
    if (!podId) {
      setDetail(null);
      setError(null);
      setTab('overview');
      setScaleForm({});
      setDiagnose(null);
      setTargetVersion('');
      setUpdateOk(null);
      setCommands(null);
      setCmdReason('');
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const r = await getEdgePod(podId);
        if (!cancelled) setDetail(r.pod);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'failed to load pod');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [podId]);

  async function refresh(): Promise<void> {
    if (!podId) return;
    setLoading(true);
    try {
      const r = await getEdgePod(podId);
      setDetail(r.pod);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'failed to refresh');
    } finally {
      setLoading(false);
    }
  }

  // Command history is fetched when the tab is first opened, not with the
  // drawer: most opens are to read telemetry, and a second round-trip on every
  // open would slow the common case to serve the rare one.
  useEffect(() => {
    if (tab !== 'commands' || !detail) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await listEdgeCommands(detail.podId);
        if (!cancelled) setCommands(r.commands);
      } catch (e) {
        if (!cancelled) {
          setCommands([]);
          setError(e instanceof Error ? e.message : 'failed to load command history');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [tab, detail]);

  async function handleQueueCommand(): Promise<void> {
    if (!detail) return;
    setCmdBusy(true);
    setError(null);
    try {
      await enqueueEdgeCommand(detail.podId, {
        command: cmdName,
        reason: cmdReason.trim(),
      });
      setCmdReason('');
      const r = await listEdgeCommands(detail.podId);
      setCommands(r.commands);
      onMutated?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'could not queue command');
    } finally {
      setCmdBusy(false);
    }
  }

  /**
   * The kill switch. `paused` is one of exactly two keys the Rust agent
   * enforces (`Directive::from_patch` in agent_control.rs reads paused /
   * scanEnabled / logLevel, and drops the last one), yet until now the only
   * control this drawer exposed was the six tuning knobs the schema itself
   * documents as "accepted, but NO AGENT READS THESE YET". The one control
   * that works had no button.
   *
   * Delivery is NOT instant and the confirm text says so. Overrides ride the
   * heartbeat RESPONSE - there is no separate poll - so how long this takes is
   * decided entirely by what the agent is running. Promising a single number
   * here is the exact mistake the Update tab used to make.
   *
   * The three cases, as of 2026-08-19, in the order an operator meets them:
   *
   *   - resident mode (agent 0.0.11+, opt-in): the agent holds a request open,
   *     so a command lands in well under a second. Measured on prod at 53 ms
   *     controller-side, 436 ms delivery-to-ack with a real agent.
   *   - the fast control tick (installs from 0.0.9+): a second schedule beats
   *     every 5 minutes by default without scanning.
   *   - anything installed BEFORE that: commands ride the scan schedule, which
   *     defaults to 240 minutes.
   *
   * Stating only the 4-hour case - which this copy did until today - is not
   * safely conservative, it is wrong in the direction that matters. An operator
   * who believes a pause needs four hours during an incident reaches for
   * something more drastic than pausing.
   */
  async function handleSetPaused(next: boolean): Promise<void> {
    if (!detail) return;
    const verb = next ? 'PAUSE scanning on' : 'RESUME scanning on';
    if (!window.confirm(
      `${verb} ${detail.podId}?\n\n` +
      `The directive is delivered on the pod's next heartbeat, not immediately. ` +
      `How long that takes depends on the agent: under a second in resident mode, ` +
      `about 5 minutes on installs from 0.0.9, and up to 4 hours on older ones ` +
      `that still ride the scan schedule. ` +
      `The row shows "awaiting pod ack" until the agent confirms it.`,
    )) return;
    setPauseBusy(true);
    setError(null);
    try {
      // Send `paused` explicitly either way. Absent would mean "inherit" -
      // the repository carries the prior value forward - so resuming has to
      // set false rather than omit the key.
      await pushEdgeConfigOverride(detail.podId, { paused: next });
      await refresh();
      onMutated?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'failed to change pause state');
    } finally {
      setPauseBusy(false);
    }
  }

  async function handleScaleSubmit(): Promise<void> {
    if (!detail) return;
    const patch: Record<string, number> = {};
    for (const [k, v] of Object.entries(scaleForm)) {
      if (v != null && Number.isFinite(v) && v > 0) patch[k] = v;
    }
    if (Object.keys(patch).length === 0) {
      setError('Provide at least one scale knob value');
      return;
    }
    setScaleBusy(true);
    setError(null);
    try {
      await pushEdgeConfigOverride(detail.podId, patch);
      setScaleForm({});
      await refresh();
      onMutated?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'failed to push config override');
    } finally {
      setScaleBusy(false);
    }
  }

  async function handleDiagnose(): Promise<void> {
    if (!detail) return;
    setDiagnoseBusy(true);
    setError(null);
    try {
      const r = await diagnoseEdgePod(detail.id);
      setDiagnose(r.report);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'diagnose failed');
    } finally {
      setDiagnoseBusy(false);
    }
  }

  async function handleDecommission(): Promise<void> {
    if (!detail) return;
    if (detail.decommissionedAt) return;
    const reason = window.prompt(
      `Decommission ${detail.displayName}? This stops the pod from joining further work. ` +
      `Enter a short reason (recorded in the audit chain):`,
      'Operator-initiated retire',
    );
    if (!reason || !reason.trim()) return;
    setDecommissionBusy(true);
    setError(null);
    try {
      const r = await decommissionEdgePod(detail.id, reason.trim());
      setDetail(r.pod);
      onMutated?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'decommission failed');
    } finally {
      setDecommissionBusy(false);
    }
  }

  async function handleUpdate(): Promise<void> {
    if (!detail || !targetVersion.trim()) return;
    // The old text promised "reconcile on its next config-override poll
    // (<=30s)". Both halves were false: there is no config-override poll
    // (overrides ride the heartbeat RESPONSE), and no edge component reads
    // `targetVersion` at all - `Directive::from_patch` in agent_control.rs
    // parses exactly paused/scanEnabled/logLevel. Recording the intent is
    // genuinely useful for the CISO change log; claiming the pod acts on it
    // is not. Corrected 2026-08-10.
    if (!window.confirm(
      `Record target version ${targetVersion.trim()} (strategy=${rolloutStrategy})? ` +
      `This is audited as an intent only - edge components do not consume ` +
      `targetVersion yet, so no pod will self-upgrade from it.`,
    )) return;
    setUpdateBusy(true);
    setError(null);
    try {
      const r = await triggerEdgeUpdate(detail.podId, {
        targetVersion: targetVersion.trim(),
        rolloutStrategy,
      });
      setUpdateOk(`Update queued - etag ${r.overrideEtag.slice(0, 8)}…`);
      setTargetVersion('');
      await refresh();
      onMutated?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'update failed');
    } finally {
      setUpdateBusy(false);
    }
  }

  const open = podId !== null;
  const ComponentIcon = detail ? (COMPONENT_ICON[detail.component] ?? Server) : Server;

  const { isPaused, pausePending } = deriveKillSwitchState(detail?.activeOverride);
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ComponentIcon className="h-4 w-4 text-primary" />
            {detail?.displayName ?? 'Loading pod…'}
            {detail && (
              <code className="font-mono text-[11px] text-muted-foreground">
                {detail.podId.slice(0, 16)}…
              </code>
            )}
          </DialogTitle>
        </DialogHeader>

        {loading && !detail && (
          <div className="py-12 flex items-center justify-center text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading pod detail…
          </div>
        )}

        {error && (
          <div className="glass-lite border border-warning/40 rounded-md p-3 text-xs flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 text-warning shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-medium">Action failed</div>
              <div className="text-muted-foreground mt-0.5">{error}</div>
            </div>
            <Button size="sm" variant="glass" onClick={() => setError(null)}>Dismiss</Button>
          </div>
        )}

        {detail && (
          <>
            {/* Tabs */}
            <div className="flex flex-wrap gap-2">
              {([
                ['overview', 'Overview', Server],
                ['commands', 'Commands', Terminal],
                ['scale', 'Scale', Sliders],
                ['diagnose', 'Diagnose', Stethoscope],
                ['update', 'Update', Upload],
              ] as const).map(([key, label, Icon]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setTab(key)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium inline-flex items-center gap-1.5 transition-colors ${
                    tab === key
                      ? 'bg-primary text-primary-foreground'
                      : 'glass-lite hover:bg-muted/30'
                  }`}
                >
                  <Icon className="h-3 w-3" />
                  {label}
                </button>
              ))}
              <div className="flex-1" />
              <Button size="sm" variant="glass" onClick={() => void refresh()} disabled={loading}>
                <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>

            <AnimatePresence mode="wait">
              {/* Overview */}
              {tab === 'overview' && (
                <motion.div
                  key="overview"
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  className="space-y-3"
                >
                  <div className="grid grid-cols-2 gap-3 text-xs">
                    <div className="glass-lite rounded p-3">
                      <div className="text-muted-foreground mb-0.5">Health</div>
                      <Badge variant={
                        detail.healthStatus === 'healthy' ? 'success'
                          : detail.healthStatus === 'down' ? 'critical'
                          : detail.healthStatus === 'stale' || detail.healthStatus === 'degraded' ? 'warning'
                          : 'glass'
                      } className="text-[10px]">{detail.healthStatus}</Badge>
                    </div>
                    <div className="glass-lite rounded p-3">
                      <div className="text-muted-foreground mb-0.5">Component</div>
                      <code className="font-mono text-[11px]">{detail.component}</code>
                    </div>
                    <div className="glass-lite rounded p-3">
                      <div className="text-muted-foreground mb-0.5">Version</div>
                      <code className="font-mono text-[11px]">{detail.version}</code>
                    </div>
                    <div className="glass-lite rounded p-3">
                      <div className="text-muted-foreground mb-0.5">Helm chart</div>
                      <code className="font-mono text-[11px]">{detail.helmChartVersion ?? '-'}</code>
                    </div>
                    <div className="glass-lite rounded p-3">
                      <div className="text-muted-foreground mb-0.5">Last heartbeat</div>
                      <code className="font-mono text-[11px]">
                        {detail.lastHeartbeatAt
                          ? new Date(detail.lastHeartbeatAt).toLocaleString()
                          : 'never'}
                      </code>
                    </div>
                    <div className="glass-lite rounded p-3">
                      <div className="text-muted-foreground mb-0.5">Cluster / namespace</div>
                      <code className="font-mono text-[11px]">
                        {detail.clusterName ?? '-'}{detail.namespace ? ` / ${detail.namespace}` : ''}
                      </code>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-3 text-xs">
                    <div className="glass-lite rounded p-3">
                      <div className="text-muted-foreground mb-0.5">Events 24h</div>
                      <div className="tabular text-sm">{detail.eventsProcessed24h.toLocaleString('en-IN')}</div>
                    </div>
                    <div className="glass-lite rounded p-3">
                      <div className="text-muted-foreground mb-0.5">Drops 24h</div>
                      <div className={`tabular text-sm ${detail.eventsDropped24h > 0 ? 'text-sev-critical' : ''}`}>
                        {detail.eventsDropped24h.toLocaleString('en-IN')}
                      </div>
                    </div>
                    <div className="glass-lite rounded p-3">
                      <div className="text-muted-foreground mb-0.5">Lag</div>
                      <div className="tabular text-sm">
                        {detail.lagWatermarkSeconds == null ? '-' : `${detail.lagWatermarkSeconds}s`}
                      </div>
                    </div>
                    {detail.component === 'detection' && (
                      <>
                        <div className="glass-lite rounded p-3">
                          <div className="text-muted-foreground mb-0.5">Decision p50</div>
                          <div className="tabular text-sm">
                            {detail.decisionRpcP50Ms == null ? '-' : `${detail.decisionRpcP50Ms}ms`}
                          </div>
                        </div>
                        <div className="glass-lite rounded p-3">
                          <div className="text-muted-foreground mb-0.5">Decision p95</div>
                          <div className="tabular text-sm">
                            {detail.decisionRpcP95Ms == null ? '-' : `${detail.decisionRpcP95Ms}ms`}
                          </div>
                        </div>
                        <div className="glass-lite rounded p-3">
                          <div className="text-muted-foreground mb-0.5">Decision p99</div>
                          <div className="tabular text-sm">
                            {detail.decisionRpcP99Ms == null ? '-' : `${detail.decisionRpcP99Ms}ms`}
                          </div>
                        </div>
                      </>
                    )}
                  </div>

                  {/*
                    THE KILL SWITCH. Until 2026-08-10 this drawer's only control was the
                    six scale knobs, which the API schema itself documents as "accepted,
                    but NO AGENT READS THESE YET" - so the one directive agents DO
                    enforce had no button, and "we can stop it remotely" was not
                    something an operator could actually do from the console.
                  */}
                  <GlassCard className="p-3">
                    <CardHeader className="p-0 mb-2">
                      <CardTitle className="text-sm inline-flex items-center gap-1.5">
                        <PauseCircle className="h-4 w-4" /> Scanning control
                      </CardTitle>
                      <CardDescription className="text-[11px]">
                        The only directive edge agents currently enforce. Delivered on the
                        pod&apos;s next heartbeat, not immediately. How long that takes is
                        set by the agent: under a second in resident mode, ~5 minutes on
                        installs from 0.0.9, up to 4 hours on older ones.
                      </CardDescription>
                    </CardHeader>
                    <div className="flex flex-wrap items-center gap-3">
                      <Badge variant={isPaused ? 'critical' : 'success'}>
                        {isPaused ? 'Paused by operator' : 'Scanning enabled'}
                      </Badge>
                      <Button
                        size="sm"
                        variant={isPaused ? 'success' : 'destructive'}
                        disabled={pauseBusy || !!detail.decommissionedAt}
                        onClick={() => void handleSetPaused(!isPaused)}
                      >
                        {pauseBusy
                          ? <Loader2 className="h-3 w-3 mr-1.5 animate-spin" />
                          : isPaused
                            ? <PlayCircle className="h-3 w-3 mr-1.5" />
                            : <PauseCircle className="h-3 w-3 mr-1.5" />}
                        {isPaused ? 'Resume scanning' : 'Pause scanning'}
                      </Button>
                      {pausePending && (
                        <span className="text-[11px] text-warning">awaiting pod ack</span>
                      )}
                    </div>
                  </GlassCard>
                  {detail.activeOverride && (
                    <GlassCard className="p-3">
                      <CardHeader className="p-0 mb-2">
                        <CardTitle className="text-sm">Active config override</CardTitle>
                        <CardDescription className="text-[11px]">
                          etag <code className="font-mono">{detail.activeOverride.overrideEtag}</code>
                          {' '}pushed {new Date(detail.activeOverride.pushedAt).toLocaleString()}
                          {detail.activeOverride.appliedAt
                            ? <> · applied {new Date(detail.activeOverride.appliedAt).toLocaleString()}</>
                            : <> · <span className="text-warning">awaiting pod ack</span></>}
                        </CardDescription>
                      </CardHeader>
                      <pre className="text-[11px] font-mono">
                        {JSON.stringify(detail.activeOverride.patch, null, 2)}
                      </pre>
                    </GlassCard>
                  )}

                  {/*
                    WHAT WE ASKED FOR vs WHAT IS RUNNING. Everything above this
                    point is desired state. Those two diverge whenever a push
                    failed, a local file overrode it, or the agent predates the
                    setting - and an operator reading desired state as though it
                    were actual concludes a scan is paused while it is still
                    running. The null case is spelled out rather than rendered
                    as defaults for exactly that reason.
                  */}
                  <GlassCard className="p-3">
                    <CardHeader className="p-0 mb-2">
                      <CardTitle className="text-sm">Actually running</CardTitle>
                      <CardDescription className="text-[11px]">
                        {detail.effectiveConfigAt
                          ? <>Self-reported by the agent {new Date(detail.effectiveConfigAt).toLocaleString()}</>
                          : <>Not reported by this agent</>}
                      </CardDescription>
                    </CardHeader>
                    {detail.effectiveConfig ? (
                      <div className="grid grid-cols-2 gap-2 text-[11px]">
                        {Object.keys(detail.effectiveConfig).sort().map((k) => (
                          <div key={k} className="glass-lite rounded px-2 py-1 flex justify-between gap-2">
                            <span className="text-muted-foreground truncate">{k}</span>
                            <code className="font-mono truncate">
                              {String((detail.effectiveConfig as Record<string, unknown>)[k])}
                            </code>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-[11px] text-muted-foreground">
                        This agent has never reported its running configuration - it predates
                        the report (agent 0.0.9+). That is <strong>not</strong> the same as
                        running defaults, so nothing is shown here rather than a guess.
                      </div>
                    )}
                  </GlassCard>
                </motion.div>
              )}

              {/* Commands (Command Plane Phase 5) */}
              {tab === 'commands' && (
                <motion.div
                  key="commands"
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  className="space-y-3"
                >
                  <div className="text-xs text-muted-foreground">
                    Commands are imperatives with a per-command acknowledgement, separate from
                    the config override above (which carries desired state and is last-write-wins).
                    They ride the pod&apos;s next heartbeat, so an ack is not immediate - but how
                    long depends on the agent: under a second in resident mode (0.0.11+, holds a
                    request open), ~5 minutes on installs from 0.0.9, and up to 4 hours on older
                    ones that still ride the scan schedule.
                  </div>

                  <GlassCard className="p-3 space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-xs text-muted-foreground block mb-0.5">Command</label>
                        <select
                          value={cmdName}
                          onChange={(e) => setCmdName(e.target.value as EdgeCommandName)}
                          className="w-full glass-lite text-sm rounded px-2 py-1 bg-background/40 border border-border/40"
                        >
                          <option value="pause">Pause scanning</option>
                          <option value="resume">Resume scanning</option>
                          <option value="collect_diagnostics">Collect diagnostics</option>
                          <option value="rescan">Rescan now</option>
                          {/*
                            Labelled, not hidden. The agent acks this `failed`
                            with "unsupported by agent <version>" - there is no
                            log-upload endpoint yet - and an operator deserves to
                            know that before spending a command, not after. Left
                            selectable because the ack is the honest record that
                            someone asked for it.
                          */}
                          <option value="upload_logs">Upload logs (no agent supports this yet)</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground block mb-0.5">
                          Reason (required)
                        </label>
                        <input
                          type="text"
                          value={cmdReason}
                          onChange={(e) => setCmdReason(e.target.value)}
                          placeholder="suspected runaway scan"
                          maxLength={500}
                          className="w-full glass-lite text-sm rounded px-2 py-1 bg-background/40 border border-border/40 focus:border-primary focus:outline-none"
                        />
                      </div>
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[10px] text-muted-foreground">
                        The reason lands in the audit chain, not just the row. &quot;Who stopped
                        this laptop and why&quot; is asked a year later, by someone who cannot
                        read the table.
                      </span>
                      <Button
                        size="sm"
                        onClick={() => void handleQueueCommand()}
                        disabled={cmdBusy || cmdReason.trim().length < 3 || !!detail.decommissionedAt}
                      >
                        {cmdBusy
                          ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                          : <Send className="h-3.5 w-3.5 mr-1" />}
                        Queue command
                      </Button>
                    </div>
                  </GlassCard>

                  {commands === null ? (
                    <div className="text-xs text-muted-foreground flex items-center gap-2">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading command history…
                    </div>
                  ) : commands.length === 0 ? (
                    <div className="text-xs text-muted-foreground">
                      No commands have been issued to this instance.
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {commands.map((c) => (
                        <div key={c.id} className="glass-lite rounded p-2 text-xs">
                          <div className="flex items-center gap-2 flex-wrap">
                            <code className="font-mono">{c.command}</code>
                            <Badge variant={COMMAND_STATE_VARIANT[c.state] ?? 'glass'} className="text-[10px]">
                              {c.state}
                            </Badge>
                            {c.result && (
                              <Badge
                                variant={c.result === 'ok' ? 'success' : 'critical'}
                                className="text-[10px]"
                              >
                                {c.result}
                              </Badge>
                            )}
                            <span className="ml-auto text-[10px] text-muted-foreground">
                              {new Date(c.createdAt).toLocaleString()}
                            </span>
                          </div>
                          {c.reason && (
                            <div className="text-muted-foreground mt-1">{c.reason}</div>
                          )}
                          {c.resultDetail && (
                            <pre className="text-[10px] font-mono mt-1 whitespace-pre-wrap break-all text-muted-foreground">
                              {c.resultDetail}
                            </pre>
                          )}
                          {/*
                            `sent` and `acked` are deliberately distinct states.
                            A command that reached a laptop which then died must
                            be distinguishable from one never delivered - those
                            need different operator responses.
                          */}
                          {c.state === 'sent' && (
                            <div className="text-[10px] text-warning mt-1">
                              delivered {c.sentAt ? new Date(c.sentAt).toLocaleString() : ''} · awaiting outcome
                              {/*
                                Repeated delivery with no ack is a different
                                problem from no delivery: the device is taking
                                commands and not answering. Retries stop after
                                5, so a stuck one says so rather than looking
                                like it is still being tried.
                              */}
                              {c.deliveryAttempts > 1 && (
                                <> · delivered {c.deliveryAttempts}x with no answer
                                  {c.deliveryAttempts >= 5 ? ' (retries exhausted)' : ''}</>
                              )}
                            </div>
                          )}
                          {c.state === 'expired' && (
                            <div className="text-[10px] text-muted-foreground mt-1">
                              never collected - the instance did not heartbeat before it expired
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </motion.div>
              )}

              {/* Scale */}
              {tab === 'scale' && (
                <motion.div
                  key="scale"
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  className="space-y-3"
                >
                  <div className="text-xs text-muted-foreground">
                    Push a Helm-values patch. Only the 6 keys below are accepted; everything else is
                    rejected server-side. The pod picks up the override on its next config-override
                    poll (≤30s).
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {([
                      ['maxMemoryMb', 'Max memory (MiB)', 64, 65536, 'Container memory limit'],
                      ['batchSize', 'Batch size', 1, 10000, 'Events per write batch'],
                      ['queueDepth', 'Queue depth', 1, 100000, 'Internal queue capacity'],
                      ['classifierTierCap', 'Classifier tier cap', 1, 5, 'Highest classifier tier to run (1=Tier1 only, 5=all)'],
                      ['clickhouseHotStoreGb', 'ClickHouse hot store (GiB)', 1, 2048, 'Per-pod hot-store hint'],
                      ['eventbusDrainIntervalMs', 'Eventbus drain (ms)', 10, 60000, 'Drain cadence'],
                    ] as const).map(([key, label, min, max, helper]) => (
                      <div key={key}>
                        <label className="text-xs text-muted-foreground block mb-0.5">{label}</label>
                        <input
                          type="number"
                          min={min}
                          max={max}
                          value={scaleForm[key] ?? ''}
                          onChange={(e) => setScaleForm((s) => ({
                            ...s,
                            [key]: e.target.value === '' ? undefined : Number(e.target.value),
                          }))}
                          placeholder={`${min}-${max}`}
                          className="w-full glass-lite text-sm rounded px-2 py-1 bg-background/40 border border-border/40 focus:border-primary focus:outline-none"
                        />
                        <div className="text-[10px] text-muted-foreground mt-0.5">{helper}</div>
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-end">
                    <Button size="sm" onClick={() => void handleScaleSubmit()} disabled={scaleBusy}>
                      {scaleBusy
                        ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                        : <Sliders className="h-3.5 w-3.5 mr-1" />}
                      Push override
                    </Button>
                  </div>
                </motion.div>
              )}

              {/* Diagnose */}
              {tab === 'diagnose' && (
                <motion.div
                  key="diagnose"
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  className="space-y-3"
                >
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-muted-foreground">
                      Runs a diagnostic check against the pod's last-known telemetry. When edge
                      components ship the pod-side /diagnose endpoint, this becomes a live RPC.
                    </div>
                    <Button size="sm" onClick={() => void handleDiagnose()} disabled={diagnoseBusy}>
                      {diagnoseBusy
                        ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                        : <Stethoscope className="h-3.5 w-3.5 mr-1" />}
                      Run diagnostic
                    </Button>
                  </div>

                  {diagnose && (
                    <GlassCard className="p-3">
                      <CardHeader className="p-0 mb-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                          Diagnostic report
                          <Badge variant={CHECK_VARIANT[diagnose.overall]} className="text-[10px]">
                            {diagnose.overall.toUpperCase()}
                          </Badge>
                          <Badge variant="glass" className="text-[10px]">
                            {diagnose.mode === 'synthesised_from_telemetry' ? 'synthesised' : 'live'}
                          </Badge>
                        </CardTitle>
                        <CardDescription className="text-[11px]">{diagnose.summary}</CardDescription>
                      </CardHeader>
                      <div className="space-y-2">
                        {diagnose.checks.map((c, i) => {
                          const Icon = CHECK_ICON[c.status];
                          return (
                            <motion.div
                              key={c.name}
                              initial={{ opacity: 0, x: -4 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: i * 0.04 }}
                              className="glass-lite rounded p-2 flex items-start gap-2 text-xs"
                            >
                              <Icon className={`h-3.5 w-3.5 shrink-0 mt-0.5 ${
                                c.status === 'ok' ? 'text-success'
                                  : c.status === 'warn' ? 'text-warning'
                                  : c.status === 'fail' ? 'text-sev-critical'
                                  : 'text-muted-foreground'
                              }`} />
                              <div className="flex-1">
                                <div className="font-medium">{c.name}</div>
                                <div className="text-muted-foreground mt-0.5">{c.detail}</div>
                              </div>
                            </motion.div>
                          );
                        })}
                      </div>
                    </GlassCard>
                  )}
                </motion.div>
              )}

              {/* Update */}
              {tab === 'update' && (
                <motion.div
                  key="update"
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  className="space-y-3"
                >
                  <div className="text-xs text-muted-foreground">
                    Record a target version. Audited as an INTENT with its rollout strategy.
                    Edge components do not consume <code>targetVersion</code> yet, so no pod
                    self-upgrades from this; use it for the change log, not to deploy.
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-muted-foreground block mb-0.5">Target version</label>
                      <input
                        type="text"
                        value={targetVersion}
                        onChange={(e) => setTargetVersion(e.target.value)}
                        placeholder="0.2.0"
                        maxLength={32}
                        className="w-full glass-lite text-sm rounded px-2 py-1 bg-background/40 border border-border/40 focus:border-primary focus:outline-none"
                      />
                      <div className="text-[10px] text-muted-foreground mt-0.5">
                        Current: <code className="font-mono">{detail.version}</code>
                      </div>
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground block mb-0.5">Rollout strategy</label>
                      <select
                        value={rolloutStrategy}
                        onChange={(e) => setRolloutStrategy(e.target.value as EdgeRolloutStrategy)}
                        className="w-full glass-lite text-sm rounded px-2 py-1 bg-background/40 border border-border/40"
                      >
                        <option value="canary">Canary (default)</option>
                        <option value="blue_green">Blue/green</option>
                        <option value="immediate">Immediate</option>
                      </select>
                      <div className="text-[10px] text-muted-foreground mt-0.5">
                        Audit-recorded; consumed by edge when wired
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    {updateOk
                      ? <Badge variant="success" className="text-[10px]"><Clock className="h-3 w-3 mr-1" />{updateOk}</Badge>
                      : <div />}
                    <Button
                      size="sm"
                      onClick={() => void handleUpdate()}
                      disabled={updateBusy || !targetVersion.trim()}
                    >
                      {updateBusy
                        ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                        : <Upload className="h-3.5 w-3.5 mr-1" />}
                      Queue update
                    </Button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </>
        )}

        <div className="flex items-center justify-between pt-2 border-t border-border/20">
          {detail && !detail.decommissionedAt ? (
            <Button
              size="sm"
              variant="destructive"
              onClick={() => void handleDecommission()}
              disabled={decommissionBusy}
              title="Retire this pod row. Audit-chained."
            >
              {decommissionBusy ? (
                <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
              ) : (
                <Trash2 className="h-3.5 w-3.5 mr-1" />
              )}
              Decommission
            </Button>
          ) : detail?.decommissionedAt ? (
            <Badge variant="glass" className="text-[10px]">
              Decommissioned · {new Date(detail.decommissionedAt).toLocaleString()}
            </Badge>
          ) : (
            <span />
          )}
          <Button size="sm" variant="glass" onClick={onClose}>
            <X className="h-3.5 w-3.5 mr-1" />
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
